/**
 * Allergen Types
 * 
 * Defines food allergens based on EU/FDA regulations
 * The 14 major allergens are listed first (EU regulation)
 */

export interface Allergen {
  id: string;
  name: string;
  icon?: string;
  /** Whether this is one of the 14 major EU allergens */
  isMajor: boolean;
}

/**
 * Predefined allergens list
 * Major allergens (EU 14) appear first, ordered by prevalence
 */
export const ALLERGENS: Allergen[] = [
  // === Major Allergens (EU 14 + FDA 9) ===
  { id: 'gluten', name: 'Gluten (cereals)', icon: '🌾', isMajor: true },
  { id: 'milk', name: 'Milk (lactose)', icon: '🥛', isMajor: true },
  { id: 'eggs', name: 'Eggs', icon: '🥚', isMajor: true },
  { id: 'peanuts', name: 'Peanuts', icon: '🥜', isMajor: true },
  { id: 'tree_nuts', name: 'Tree nuts', icon: '🌰', isMajor: true },
  { id: 'fish', name: 'Fish', icon: '🐟', isMajor: true },
  { id: 'crustaceans', name: 'Crustaceans', icon: '🦐', isMajor: true },
  { id: 'molluscs', name: 'Molluscs', icon: '🦪', isMajor: true },
  { id: 'soy', name: 'Soybeans', icon: '🫘', isMajor: true },
  { id: 'celery', name: 'Celery', icon: '🥬', isMajor: true },
  { id: 'mustard', name: 'Mustard', icon: '🟡', isMajor: true },
  { id: 'sesame', name: 'Sesame seeds', icon: '⚪', isMajor: true },
  { id: 'sulphites', name: 'Sulphur dioxide / Sulphites', icon: '🧪', isMajor: true },
  { id: 'lupin', name: 'Lupin', icon: '🌸', isMajor: true },
  
  // === Additional common allergens ===
  { id: 'corn', name: 'Corn (maize)', icon: '🌽', isMajor: false },
  { id: 'coconut', name: 'Coconut', icon: '🥥', isMajor: false },
  { id: 'garlic', name: 'Garlic', icon: '🧄', isMajor: false },
  { id: 'onion', name: 'Onion', icon: '🧅', isMajor: false },
  { id: 'nightshades', name: 'Nightshades (tomato, pepper)', icon: '🍅', isMajor: false },
  { id: 'citrus', name: 'Citrus fruits', icon: '🍋', isMajor: false },
  { id: 'kiwi', name: 'Kiwi', icon: '🥝', isMajor: false },
  { id: 'banana', name: 'Banana', icon: '🍌', isMajor: false },
  { id: 'avocado', name: 'Avocado', icon: '🥑', isMajor: false },
  { id: 'mango', name: 'Mango', icon: '🥭', isMajor: false },
  { id: 'latex', name: 'Latex (cross-reactive)', icon: '🧤', isMajor: false },
];

/**
 * Get allergen by ID
 */
export function getAllergenById(id: string): Allergen | undefined {
  return ALLERGENS.find(a => a.id === id);
}

/**
 * Get allergen names from IDs
 */
export function getAllergenNames(ids: string[]): string[] {
  return ids
    .map(id => getAllergenById(id)?.name)
    .filter((name): name is string => !!name);
}
